import { TicketsWorkspaceText, TicketsWorkspaceTranslateFn } from './types';

export function buildTicketsWorkspaceText(params: {
  isAdminVariant: boolean;
  t: TicketsWorkspaceTranslateFn;
  maxAttachmentsPerMessage: number;
}): TicketsWorkspaceText {
  const { isAdminVariant, t, maxAttachmentsPerMessage } = params;

  if (isAdminVariant) {
    return {
      statusFilters: [
        { value: 'all', label: 'All' },
        { value: 'open', label: 'Open' },
        { value: 'atendimento', label: 'In Progress' },
        { value: 'finalized', label: 'Finalized' },
      ],
      priorityFilters: [
        { value: 'all', label: 'All priorities' },
        { value: 'low', label: 'Low' },
        { value: 'high', label: 'High' },
        { value: 'critical', label: 'Critical' },
      ],
      statusLabels: {
        open: 'Open',
        atendimento: 'In Progress',
        finalized: 'Finalized',
      },
      priorityLabels: {
        low: 'Low',
        high: 'High',
        critical: 'Critical',
      },
      supportSuffix: 'Support',
      userFallback: (id: number) => `User #${id}`,
      blockedFinalized:
        'This ticket is finalized and does not accept new messages.',
      blockedAwaiting:
        'Wait for support to reply before sending another message.',
      invalidFileType: (name: string) =>
        `${name}: invalid file type. Use JPG, PNG or WEBP.`,
      maxFileSize: (name: string) => `${name}: maximum size is 2MB.`,
      maxImagesPerMessage: `Limit of ${maxAttachmentsPerMessage} images per message.`,
      onlyAdminsCanCreate: 'Only admins can create tickets.',
      ticketSubjectRequired: 'Please provide a ticket subject.',
      addMessageOrImage: 'Add a message or at least one image.',
      ticketCreatedSuccess: 'Ticket created successfully.',
      createTicketFailed: 'Could not create the ticket.',
      selectTicketToReply: 'Select a ticket to reply.',
      messageSentSuccess: 'Message sent successfully.',
      sendMessageConflict: 'Ticket business rule conflict.',
      sendMessageFailed: 'Could not send the message.',
      ticketFinalizedSuccess: 'Ticket finalized successfully.',
      ticketAlreadyFinalized: 'This ticket is already finalized.',
      finalizeTicketFailed: 'Could not finalize the ticket.',
      ticketReopenedSuccess: 'Ticket reopened successfully.',
      ticketNotFinalized: 'Only finalized tickets can be reopened.',
      reopenTicketFailed: 'Could not reopen the ticket.',
      downloadLinkFailed: 'Could not generate the download link.',
      accessDenied:
        'Your user does not have access to the tickets module (admin or root only).',
      selectTicketToView: 'Select a ticket to view the conversation.',
      detailLoadFailed: 'Could not load ticket details.',
      retry: 'Try again',
      noMessages: 'This ticket has no messages yet.',
      noPermissionToViewTickets: 'You do not have permission to view tickets.',
      ticketListLoadFailed: 'Could not load the ticket list.',
      noTicketsFound: 'No tickets found for this filter.',
      title: 'Support Tickets',
      descriptionRoot: 'View and reply to tickets from all stores.',
      descriptionAdmin: 'Open tickets and track support replies.',
      refresh: 'Refresh',
      openNewTicket: 'Open New Ticket',
      subjectLabel: 'Subject',
      subjectPlaceholder: 'Ex: Product sync error',
      priorityLabel: 'Priority',
      messageLabel: 'Message',
      messagePlaceholder: 'Describe the issue in detail...',
      imagesLabel: `Images (up to ${maxAttachmentsPerMessage})`,
      imagesHint: 'Formats: JPG, PNG and WEBP. Maximum 2MB per file.',
      createTicketButton: 'Create Ticket',
      createAttachFilesButton: 'Attach files',
      ticketListTitle: 'Ticket List',
      filterTitle: 'Filters',
      filterStatusLabel: 'Status',
      filterPriorityLabel: 'Priority',
      filterSubjectLabel: 'Subject',
      filterSubjectPlaceholder: 'Ex: Product sync',
      filterDescriptionLabel: 'Description',
      filterDescriptionPlaceholder: 'Ex: timeout on upload',
      filterCustomerIdLabel: 'Customer ID',
      filterCustomerIdPlaceholder: 'Ex: 123',
      clearFiltersButton: 'Clear filters',
      pageLabel: (currentPage: number, totalPages: number) =>
        `Page ${currentPage} of ${totalPages}`,
      previousButton: 'Previous',
      nextButton: 'Next',
      conversationTitle: 'Conversation',
      finalizeTicketButton: 'Finalize Ticket',
      reopenTicketButton: 'Reopen Ticket',
      updatePriorityButton: 'Update Priority',
      priorityUpdatePendingButton: 'Updating...',
      createdAtLabel: (date: string) => `Created at ${date}`,
      finalizedAtLabel: (date: string) => `Finalized at ${date}`,
      currentPriorityLabel: (priority: string) =>
        `Current priority: ${priority}`,
      initialPriorityLabel: (priority: string) =>
        `Initial priority: ${priority}`,
      lastPriorityUpdateLabel: (name: string, date: string) =>
        `Last priority update by ${name} at ${date}`,
      neverUpdatedPriorityLabel: 'No priority updates yet.',
      replyTicketTitle: 'Reply Ticket',
      replyPlaceholder: 'Type your response...',
      replyAttachFilesButton: 'Attach files',
      replyImagesHint: `Up to ${maxAttachmentsPerMessage} images per message.`,
      sendMessageButton: 'Send Message',
      ticketUpdatedAtLabel: (id: number, date: string) =>
        `Ticket #${id} - Updated at ${date}`,
      awaitingSupportReply: 'Waiting for support reply.',
      ticketPriorityUpdatedSuccess: 'Ticket priority updated successfully.',
      ticketPriorityUpdateFailed: 'Could not update ticket priority.',
      slaTitle: 'SLA Priority',
      slaDescription:
        'The ticket priority defines the maximum response time for support.',
      slaSeverityHeader: 'Severity',
      slaResponseHeader: 'Maximum response time',
      slaResponseByPriority: {
        low: '24 business hours',
        high: '12 business hours',
        critical: '4 business hours',
      },
    };
  }

  return {
    statusFilters: [
      { value: 'all', label: t('filters.all') },
      { value: 'open', label: t('filters.open') },
      { value: 'atendimento', label: t('filters.inProgress') },
      { value: 'finalized', label: t('filters.finalized') },
    ],
    priorityFilters: [
      { value: 'all', label: t('priorityFilters.all') },
      { value: 'low', label: t('priority.low') },
      { value: 'high', label: t('priority.high') },
      { value: 'critical', label: t('priority.critical') },
    ],
    statusLabels: {
      open: t('status.open'),
      atendimento: t('status.inProgress'),
      finalized: t('status.finalized'),
    },
    priorityLabels: {
      low: t('priority.low'),
      high: t('priority.high'),
      critical: t('priority.critical'),
    },
    supportSuffix: t('authors.supportSuffix'),
    userFallback: (id: number) => t('authors.userFallback', { id }),
    blockedFinalized: t('messages.blockedFinalized'),
    blockedAwaiting: t('messages.blockedAwaiting'),
    invalidFileType: (name: string) => t('toasts.invalidFileType', { name }),
    maxFileSize: (name: string) => t('toasts.maxFileSize', { name }),
    maxImagesPerMessage: t('toasts.maxImagesPerMessage', {
      count: maxAttachmentsPerMessage,
    }),
    onlyAdminsCanCreate: t('toasts.onlyAdminsCanCreate'),
    ticketSubjectRequired: t('toasts.ticketSubjectRequired'),
    addMessageOrImage: t('toasts.addMessageOrImage'),
    ticketCreatedSuccess: t('toasts.ticketCreatedSuccess'),
    createTicketFailed: t('toasts.createTicketFailed'),
    selectTicketToReply: t('toasts.selectTicketToReply'),
    messageSentSuccess: t('toasts.messageSentSuccess'),
    sendMessageConflict: t('toasts.sendMessageConflict'),
    sendMessageFailed: t('toasts.sendMessageFailed'),
    ticketFinalizedSuccess: t('toasts.ticketFinalizedSuccess'),
    ticketAlreadyFinalized: t('toasts.ticketAlreadyFinalized'),
    finalizeTicketFailed: t('toasts.finalizeTicketFailed'),
    ticketReopenedSuccess: t('toasts.ticketReopenedSuccess'),
    ticketNotFinalized: t('toasts.ticketNotFinalized'),
    reopenTicketFailed: t('toasts.reopenTicketFailed'),
    downloadLinkFailed: t('toasts.downloadLinkFailed'),
    accessDenied: t('accessDenied'),
    selectTicketToView: t('empty.selectTicketToView'),
    detailLoadFailed: t('errors.detailLoadFailed'),
    retry: t('actions.retry'),
    noMessages: t('empty.noMessages'),
    noPermissionToViewTickets: t('errors.noPermissionToViewTickets'),
    ticketListLoadFailed: t('errors.ticketListLoadFailed'),
    noTicketsFound: t('empty.noTicketsFound'),
    title: t('header.title'),
    descriptionRoot: t('header.descriptionRoot'),
    descriptionAdmin: t('header.descriptionAdmin'),
    refresh: t('actions.refresh'),
    openNewTicket: t('create.openNewTicket'),
    subjectLabel: t('create.subjectLabel'),
    subjectPlaceholder: t('create.subjectPlaceholder'),
    priorityLabel: t('create.priorityLabel'),
    messageLabel: t('create.messageLabel'),
    messagePlaceholder: t('create.messagePlaceholder'),
    imagesLabel: t('create.imagesLabel', {
      count: maxAttachmentsPerMessage,
    }),
    imagesHint: t('create.imagesHint'),
    createTicketButton: t('create.createTicketButton'),
    createAttachFilesButton: t('create.attachFilesButton'),
    ticketListTitle: t('list.title'),
    filterTitle: t('list.filters.title'),
    filterStatusLabel: t('list.filters.status'),
    filterPriorityLabel: t('list.filters.priority'),
    filterSubjectLabel: t('list.filters.subject'),
    filterSubjectPlaceholder: t('list.filters.subjectPlaceholder'),
    filterDescriptionLabel: t('list.filters.description'),
    filterDescriptionPlaceholder: t('list.filters.descriptionPlaceholder'),
    filterCustomerIdLabel: t('list.filters.customerId'),
    filterCustomerIdPlaceholder: t('list.filters.customerIdPlaceholder'),
    clearFiltersButton: t('list.filters.clear'),
    pageLabel: (currentPage: number, totalPages: number) =>
      t('list.pagination', { currentPage, totalPages }),
    previousButton: t('actions.previous'),
    nextButton: t('actions.next'),
    conversationTitle: t('conversation.title'),
    finalizeTicketButton: t('conversation.finalizeTicketButton'),
    reopenTicketButton: t('conversation.reopenTicketButton'),
    updatePriorityButton: t('conversation.updatePriorityButton'),
    priorityUpdatePendingButton: t('conversation.priorityUpdatePendingButton'),
    createdAtLabel: (date: string) => t('conversation.createdAt', { date }),
    finalizedAtLabel: (date: string) => t('conversation.finalizedAt', { date }),
    currentPriorityLabel: (priority: string) =>
      t('conversation.currentPriority', { priority }),
    initialPriorityLabel: (priority: string) =>
      t('conversation.initialPriority', { priority }),
    lastPriorityUpdateLabel: (name: string, date: string) =>
      t('conversation.lastPriorityUpdate', { name, date }),
    neverUpdatedPriorityLabel: t('conversation.neverUpdatedPriority'),
    replyTicketTitle: t('reply.title'),
    replyPlaceholder: t('reply.placeholder'),
    replyAttachFilesButton: t('reply.attachFilesButton'),
    replyImagesHint: t('reply.imagesHint', {
      count: maxAttachmentsPerMessage,
    }),
    sendMessageButton: t('reply.sendMessageButton'),
    ticketUpdatedAtLabel: (id: number, date: string) =>
      t('list.ticketUpdatedAt', { id, date }),
    awaitingSupportReply: t('list.awaitingSupportReply'),
    ticketPriorityUpdatedSuccess: t('toasts.ticketPriorityUpdatedSuccess'),
    ticketPriorityUpdateFailed: t('toasts.ticketPriorityUpdateFailed'),
    slaTitle: t('sla.title'),
    slaDescription: t('sla.description'),
    slaSeverityHeader: t('sla.severityHeader'),
    slaResponseHeader: t('sla.responseHeader'),
    slaResponseByPriority: {
      low: t('sla.lowResponse'),
      high: t('sla.highResponse'),
      critical: t('sla.criticalResponse'),
    },
  };
}
